import React from 'react';

interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  status?: 'online' | 'offline' | 'busy' | 'away';
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  status,
  className = ''
}) => {
  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl'
  };

  const statusSizes = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
    xl: 'w-4 h-4'
  };

  const statusColors = {
    online: 'bg-success-500',
    offline: 'bg-secondary-400',
    busy: 'bg-danger-500',
    away: 'bg-warning-500'
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={`relative inline-flex ${className}`}>
      {src ? (
        <img
          src={src}
          alt={alt || name}
          className={`${sizes[size]} rounded-full object-cover`}
        />
      ) : (
        <div className={`
          ${sizes[size]} rounded-full bg-primary-100 text-primary-600 
          font-semibold flex items-center justify-center
        `}>
          {name ? getInitials(name) : '?'}
        </div>
      )}
      {status && (
        <span className={`
          absolute bottom-0 right-0 ${statusSizes[size]} ${statusColors[status]}
          rounded-full border-2 border-white
        `} />
      )}
    </div>
  );
};

interface AvatarGroupProps {
  avatars: Array<{ src?: string; name?: string }>;
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  avatars,
  max = 4,
  size = 'sm'
}) => {
  const visible = avatars.slice(0, max);
  const remaining = avatars.length - max;

  const sizes = {
    xs: 'w-6 h-6 text-xs -ml-2',
    sm: 'w-8 h-8 text-sm -ml-2',
    md: 'w-10 h-10 text-base -ml-3',
    lg: 'w-12 h-12 text-lg -ml-4'
  };

  return (
    <div className="flex items-center">
      {visible.map((avatar, i) => (
        <div key={i} className={`${i > 0} ${sizes[size]} ring-2 ring-white rounded-full`}>
          <Avatar src={avatar.src} name={avatar.name} size={size} />
        </div>
      ))}
      {remaining > 0 && (
        <div className={`
          ${sizes[size]} rounded-full bg-secondary-200 text-secondary-600 
          font-medium flex items-center justify-center ring-2 ring-white
        `}>
          +{remaining}
        </div>
      )}
    </div>
  );
};
